import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, MessageCircle } from 'lucide-react';
import { getCart, removeFromCart, updateCartQuantity, generateWhatsAppCartMessage, clearCart } from '../utils/cart';
import { toast } from 'sonner';
import { normalizeImageUrl } from '../utils/imageUtils'; // ✅ HEIC fix

const WA_NUMBER = '919538769669';

const CartPage = () => {
  const [cart, setCart] = useState([]);

  useEffect(() => { loadCart(); }, []);

  const loadCart = () => setCart(getCart());

  const handleRemove = (sku) => {
    removeFromCart(sku);
    loadCart();
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success('Removed from cart');
  };

  const handleQuantityChange = (sku, newQuantity) => {
    if (newQuantity < 1) return;
    updateCartQuantity(sku, newQuantity);
    loadCart();
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleWhatsAppOrder = () => {
    if (cart.length === 0) { toast.error('Your cart is empty'); return; }
    const message = generateWhatsAppCartMessage(cart);
    window.open(`https://wa.me/${WA_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9] py-12">
      <div className="container mx-auto px-4 md:px-8">
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#2A0000] mb-8" data-testid="cart-page-title">
          Your Wishlist Cart
        </h1>

        {cart.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg mb-6">Your cart is empty</p>
            <Link to="/collections" className="btn-primary">Continue Shopping</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">

            {/* ── Cart Items ── */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item) => (
                <div
                  key={item.sku}
                  className="bg-white p-6 rounded-sm border border-[#E5A93C]/20 flex gap-6"
                  data-testid={`cart-item-${item.sku}`}
                >
                  <Link to={`/product/${item.sku}`} className="flex-shrink-0">
                    {/* ✅ normalizeImageUrl converts HEIC → f_auto for cart thumbnails */}
                    <img
                      src={normalizeImageUrl(item.images?.[0] || 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg')}
                      alt={item.title}
                      className="w-24 h-24 object-cover rounded-sm hover:opacity-90 transition-opacity"
                      onError={(e) => {
                        e.target.src = 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg';
                      }}
                    />
                  </Link>

                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.sku}`}>
                      <h3 className="font-semibold text-[#2C2C2C] mb-1 hover:text-[#2A0000] transition-colors">
                        {item.title}
                      </h3>
                    </Link>
                    <p className="text-sm text-gray-500 mb-2">{item.sku} · {item.color}</p>
                    <p className="text-sm font-semibold text-[#25D366]">Price on WhatsApp</p>
                  </div>

                  <div className="flex flex-col items-end justify-between flex-shrink-0">
                    <button
                      onClick={() => handleRemove(item.sku)}
                      className="text-red-400 hover:text-red-600 transition-colors"
                      data-testid={`remove-${item.sku}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>

                    <div className="flex items-center space-x-1 border border-gray-200 rounded-sm">
                      <button
                        onClick={() => handleQuantityChange(item.sku, item.quantity - 1)}
                        className="p-2 hover:bg-gray-100 transition-colors"
                        data-testid={`decrease-${item.sku}`}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="px-3 text-sm font-medium min-w-[2rem] text-center" data-testid={`quantity-${item.sku}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => handleQuantityChange(item.sku, item.quantity + 1)}
                        className="p-2 hover:bg-gray-100 transition-colors"
                        data-testid={`increase-${item.sku}`}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Order Summary ── */}
            <div>
              <div className="bg-white p-6 rounded-sm border border-[#E5A93C]/20 sticky top-24 space-y-4">
                <h3 className="font-heading text-xl font-bold text-[#2A0000]">Order Summary</h3>

                <div className="space-y-2 text-sm border-b pb-4">
                  {cart.map(item => (
                    <div key={item.sku} className="flex justify-between text-gray-600">
                      <span className="truncate max-w-[200px]">{item.title} × {item.quantity}</span>
                    </div>
                  ))}
                </div>

                <p className="text-sm text-gray-500">
                  {cart.reduce((n, i) => n + i.quantity, 0)} item{cart.length !== 1 ? 's' : ''} in cart — prices confirmed on WhatsApp.
                </p>

                <button
                  onClick={handleWhatsAppOrder}
                  className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 rounded-sm hover:bg-[#20BA5A] transition-colors font-medium"
                  data-testid="whatsapp-order-button"
                >
                  <MessageCircle className="w-4 h-4" />
                  Enquire & Order on WhatsApp
                </button>

                <Link to="/collections" className="block text-center text-[#E5A93C] hover:underline text-sm">
                  Continue Shopping
                </Link>
              </div>
            </div>

          </div>
        )}

        {cart.length > 0 && (
          <div className="mt-16">
            <h2 className="font-heading text-2xl font-bold text-[#2A0000] mb-2">You May Also Like</h2>
            <p className="text-gray-500 text-sm">Complete your look with these matching pieces</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;